import React, { Children, useState } from 'react';
import { useSelector } from 'react-redux';
import { Icon, Spinner } from '@iq/react-components';
import HealthRiskItem from './HealthListItem';
import { STATUSES } from '../../../constants';
import {
  getActiveComponent,
  getComponentsStatus,
  getComponentsStatusUpdatedAt,
} from '../../../bundles/components';
import { getPageType, useDateSubscription } from '../../../bundles/application';
import { getSources } from '../../../bundles/sources';
import RiskModal from './RiskModal';
import RiskFilters from './RiskFilters/RiskFilters';

const HealthPanel = ({
  showComponentName = true,
  showStatus = true,
  showSuggestions = true,
  format,
  showRiskIfNoActiveComponent = true,
  panelId,
}) => {
  const componentsStatus = useSelector(getComponentsStatus);
  const componentsStatusUpdatedAt = useSelector(getComponentsStatusUpdatedAt);
  const { id, name, itemDesignation, icon } = useSelector(getActiveComponent) || {};
  const pageType = useSelector(getPageType);
  const sources = useSelector(getSources);
  const pollingDateRange = useDateSubscription(panelId);
  const elasticSearchSource = sources.find((s) => s.type === 'elasticsearch');
  const riskThreshold = elasticSearchSource?.options?.riskThreshold || {};
  const [riskSearch, setRiskSearch] = useState('');
  const [openedRisk, setOpenedRisk] = useState();
  const [filterFormData, setFilterFormData] = useState({
    riskLevels: {
      showLowRisk: true,
      showMediumRisk: true,
      showHighRisk: true,
    },
  });

  const showAllRisks = showRiskIfNoActiveComponent && !id;

  if (!showRiskIfNoActiveComponent && !id) {
    return <div className="health-panel-component empty">No component selected</div>;
  }
  const componentHealthStatus = componentsStatus.find((c) => c.id === id) || {};

  const getStatusColor = (s) => (STATUSES[s] || STATUSES.default).color;
  const getStatusIcon = (s) => (STATUSES[s] || STATUSES.default).icon;
  const getRGB = (s) => (STATUSES[s] || STATUSES.default).color.join();

  const shouldShowRiskStatus = (status) => {
    switch (status) {
      case 'r1':
        return filterFormData.riskLevels.showLowRisk;
      case 'r2':
        return filterFormData.riskLevels.showMediumRisk;
      case 'r3':
        return filterFormData.riskLevels.showHighRisk;
      default:
        return false;
    }
  };

  const filterRiskStatus = (eventRiskStatus) =>
    eventRiskStatus?.filter((e) => {
      const concatString = `${e.event.remoteSource}${e.eventText}${e.pointId}`;
      return (
        concatString.toLowerCase().includes(riskSearch.trim().toLowerCase()) &&
        shouldShowRiskStatus(e.status) &&
        e.eventDateTime >= pollingDateRange.startDate &&
        e.eventDateTime <= pollingDateRange.endDate
      );
    }) || [];

  const getContent = () => {
    const contentObj = {
      // Handle for None and StateSet page types in future if needed
      risk: {
        showRiskStatus: showStatus,
        riskStatus: showAllRisks
          ? componentsStatus
              .filter((c) => c?.eventRiskStatus?.length > 0)
              .flatMap((r) => filterRiskStatus(r.eventRiskStatus))
          : filterRiskStatus(
              componentsStatus.find((c) => c.id === id && c.eventRiskStatus?.length > 0)
                ?.eventRiskStatus
            ),
      },
      health: {
        showHealthStatus: showStatus,
        healthStatus: componentHealthStatus?.status,
        statusText: componentHealthStatus?.statusText,
        healthSuggestions: componentHealthStatus?.suggestions,
      },
    };
    return contentObj[pageType] || {};
  };

  const {
    showHealthStatus,
    healthStatus,
    healthSuggestions,
    statusText,
    showRiskStatus,
    riskStatus,
  } = getContent();

  if (!componentsStatusUpdatedAt) {
    return (
      <div className={`health-panel-component ${format}`}>
        <div className="loading-container">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className={`health-panel-component custom-scrollbar ${format}`}>
      {showRiskStatus && (
        <div className="filters-header">
          <RiskFilters
            filterFormData={filterFormData}
            setFilterFormData={setFilterFormData}
            riskSearch={riskSearch}
            onSearch={(e) => {
              setRiskSearch(e.target.value);
            }}
            panelId={panelId}
          />
        </div>
      )}
      {showComponentName && !showAllRisks && (
        <div className="component-header">
          <div>
            <div className="component-name">{name}</div>
            <div className="component-id">{itemDesignation}</div>
          </div>
          <Icon icon={icon} />
        </div>
      )}
      {!showHealthStatus && !showRiskStatus && (
        <div className="component-status--empty">No status available</div>
      )}
      {showHealthStatus &&
        (healthStatus ? (
          <div
            className="component-status--wrapper"
            style={{
              backgroundColor: `rgba(${getRGB(healthStatus)}, ${
                getStatusColor(healthStatus) ? 1 : 0
              })`,
            }}
          >
            <div className="component-status--icon">
              <Icon icon={getStatusIcon(healthStatus)} />
            </div>
            <div className="component-status--text">{statusText}</div>
          </div>
        ) : (
          <div className="component-status--empty">No Health available</div>
        ))}
      {showSuggestions && healthSuggestions && (
        <div className="component-suggestion--wrapper">
          <div className="component-suggestion--title">Suggestions</div>
          {healthSuggestions.length !== 0 ? (
            <ul className="component-suggestion--list">
              {healthSuggestions.map((suggestion, i) => (
                <li key={i}>{suggestion}</li>
              ))}
            </ul>
          ) : (
            <div className="component-suggestion--empty">No suggestions</div>
          )}
        </div>
      )}
      {showRiskStatus && (
        <div className="component-suggestion--wrapper custom-scrollbar">
          {riskStatus && riskStatus.length !== 0 ? (
            <div className="risk-list">
              {Children.toArray(
                riskStatus
                  .sort((x, y) => y.eventDateTime - x.eventDateTime)
                  .map((risk) => (
                    <HealthRiskItem
                      risk={risk}
                      riskThreshold={riskThreshold}
                      onClick={() => {
                        setOpenedRisk(risk);
                      }}
                    />
                  ))
              )}
            </div>
          ) : (
            <div className="component-status--empty risk-panel">
              <div className="component-status--heading">No risks to display.</div>
              <div className="component-status--text">
                The Risk Panel displays data for risks identified within the past 12 months.
              </div>
              <div className="component-status--text">
                If the date range is valid and no risks are shown, it may indicate that no risks
                were recorded for the selected timeframe.
              </div>
            </div>
          )}
        </div>
      )}
      {openedRisk && (
        <RiskModal
          openedRisk={openedRisk}
          setOpenedRisk={setOpenedRisk}
          riskThreshold={riskThreshold}
        />
      )}
    </div>
  );
};

export default HealthPanel;
